import { useEffect, useState } from "react"
import { IconType } from "react-icons";
import { MdOutlinePayments } from "react-icons/md";
import { IoDocumentTextOutline, IoLibraryOutline, IoPersonOutline, IoRibbonOutline, IoWarning } from "react-icons/io5";
import { FaRegMoneyBill1 } from "react-icons/fa6";
import { AiOutlineSignature } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom"

import { HStack, Icon, Image, Spacer, Spinner, Stack, VStack } from "@chakra-ui/react"
import { NAV_OPTIONS, NAV_OPTIONS_NO_DEALS } from "@/constants"
import { useAuth } from "@/contexts"
import { useDropboxDocuments } from "@/hooks"
import { Deal, DealManager, DealStep, NavOption } from "@/types"
import { getPaymentsClientByEmail, listPaymentsBankAccounts } from "@/api/payments"
import { getSecondaryDeals } from "@/api"
import { Button, PulsingDot, Text } from "../ui"

type NavIndicator = "dot" | "warning" | "spinner"

const NavItem = ({ label, isActive, indicator, onNavItemClick }: { label: string; isActive: boolean; indicator?: NavIndicator; onNavItemClick?: () => void }) => {
  const navigate = useNavigate()

  const convertUnderscoresToSpaces = (str: string) => {
    if (!str) return ""
    if (str === NavOption.TAX_CALCULATION) {
      return "Calculate Your Taxes";
    }
    if (str === NavOption.SECONDARY) {
      return "Secondary Fund";
    }
    return str
    .split("_")
    .map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
  }

  const getIconForLabel = (label: string): IconType => {
    switch(label) {
      case NavOption.DOCUMENTS:
        return IoDocumentTextOutline;
      case NavOption.COMPLIANCE:
        return IoRibbonOutline;
      case NavOption.CURRENT_OFFER:
        return AiOutlineSignature;
      case NavOption.TAX_CALCULATION:
        return FaRegMoneyBill1;
      case NavOption.RESOURCES:
        return IoLibraryOutline;
      case NavOption.PAYMENT_CENTER:
        return MdOutlinePayments;
      case NavOption.SECONDARY:
        return MdOutlinePayments;
      case NavOption.ACCOUNT:
        return IoPersonOutline;
      default:
        return IoDocumentTextOutline;
    }
  }

  return (
    <Stack
      mx={3}
      px={3}
      py={2}
      borderRadius={5}
      backgroundColor={isActive ? "whiteAlpha.100" : "transparent"}
      cursor="pointer"
      _hover={{ backgroundColor: "whiteAlpha.100" }}
      onClick={() => { navigate(`/${label === NavOption.TAX_CALCULATION ? "taxes" : label === NavOption.CURRENT_OFFER ? "deals" : label === NavOption.PAYMENT_CENTER ? "payment" : label}`); onNavItemClick?.() }}
    >
      <HStack>
        <Icon as={getIconForLabel(label) as React.ElementType} size="sm" color={isActive ? "white" : ""} />
        <Text fontSize="sm" fontWeight="medium" color={isActive ? "white" : "darkBlue.700"}>
          {convertUnderscoresToSpaces(label)}
        </Text>
        {indicator === "dot" && <><Spacer /><PulsingDot /></>}
        {indicator === "warning" && <><Spacer /><Icon as={IoWarning} boxSize={4} color="yellow.400" /></>}
        {indicator === "spinner" && <><Spacer /><Spinner size="xs" color="whiteAlpha.700" /></>}
      </HStack>
    </Stack>
  )
}

interface Props {
  deals: Deal[]
  // Optional: secondary-only clients are seeded without an assigned DM.
  dealManager?: DealManager
  onNavItemClick?: () => void
}

export const Navigation = ({ deals, dealManager, onNavItemClick }: Props) => {
  const location = useLocation()
  const checkIsCurrentNavOption = (option: string) => {
    if (option === NavOption.TAX_CALCULATION) {
      return location.pathname === "/taxes"
    }

    if (option === NavOption.CURRENT_OFFER) {
      return location.pathname === "/deals"
    }

    if (option === NavOption.PAYMENT_CENTER) {
      return location.pathname === "/payment"
    }
    
    return location.pathname === `/${option}`
  }

  const { user } = useAuth()

  // ── Portal rollout gating ──
  // While the portal is in limited rollout, a client without full access only gets
  // the Payment Center (+ the account/profile menu & 2FA in the top bar). Full
  // access is unlocked server-side when their intro/welcome email is sent
  // (fullPortalAccess === true), or once the rollout flag is removed entirely.
  // The Payment Center itself is shown to all clients.
  const clientUser = user?.userType === "client" ? user : undefined
  const isLocked = clientUser?.fullPortalAccess === false
  const showPaymentCenter = deals.some(d => d.isActive === 1)

  // Check if client has a connected bank account — show notification dot if not
  const [hasConnectedBank, setHasConnectedBank] = useState(false)
  const [bankCheckDone, setBankCheckDone] = useState(false)
  useEffect(() => {
    if (!showPaymentCenter || !user?.email) return
    getPaymentsClientByEmail(user.email)
      .then(paymentsClient => {
        if (!paymentsClient) { setBankCheckDone(true); return }
        return listPaymentsBankAccounts(paymentsClient.id)
          .then(accounts => setHasConnectedBank(accounts.length > 0))
          .catch(() => setHasConnectedBank(false))
      })
      .catch(() => setHasConnectedBank(false))
      .finally(() => setBankCheckDone(true))
  }, [showPaymentCenter, user?.email])

  const showPaymentCenterDot = showPaymentCenter && bankCheckDone && !hasConnectedBank
  const showComplianceDot = deals?.some(deal => deal.currentStep === DealStep.COMPLETE_BC_FORM)

  // NDA Dropbox check for Compliance warning indicator
  const activeDeal = deals.find(d => d.isActive === 1)
  const ndaFormDone = activeDeal && activeDeal.currentStep !== DealStep.COMPLETE_NDA_FORM
  const { data: ndaDocs, isLoading: isLoadingNdaDocs } = useDropboxDocuments(ndaFormDone ? activeDeal?.id : undefined)
  const ndaInDropbox = ndaDocs?.some(doc => doc.name.toLowerCase().includes("eso mutual nda")) ?? false

  const getComplianceIndicator = (): NavIndicator | undefined => {
    if (ndaFormDone && isLoadingNdaDocs) return "spinner"
    if (ndaFormDone && !ndaInDropbox) return "warning"
    if (showComplianceDot) return "dot"
    return undefined
  }

  // Secondary Fund payment center — shown only to clients that actually have
  // secondary deals (from the Airtable mirror; a 4xx/empty response hides it).
  const [hasSecondaryDeals, setHasSecondaryDeals] = useState(false)
  useEffect(() => {
    if (!clientUser?.email) return
    getSecondaryDeals()
      .then(res => setHasSecondaryDeals(res.deals.length > 0))
      .catch(() => setHasSecondaryDeals(false))
  }, [clientUser?.email])

  const baseNavOptions = deals && deals.length > 0 ? NAV_OPTIONS : NAV_OPTIONS_NO_DEALS
  const navOptionsToRender = isLocked
    ? [...(showPaymentCenter ? [NavOption.PAYMENT_CENTER] : []), ...(hasSecondaryDeals ? [NavOption.SECONDARY] : []), NavOption.ACCOUNT]
    : [...baseNavOptions, ...(showPaymentCenter ? [NavOption.PAYMENT_CENTER] : []), ...(hasSecondaryDeals ? [NavOption.SECONDARY] : [])]
  const safeNavOptions = navOptionsToRender.filter(Boolean)
  return (
    <Stack h="100%" bg="darkBlue.200" color="white" pb={5}>
      <Text fontSize="xl" fontWeight="bold" pt={5} pl={5} pb={5} fontFamily={"body"}>
        ESO Fund Client Portal
      </Text>
      {safeNavOptions.map((option) => {
        let indicator: NavIndicator | undefined
        if (option === NavOption.PAYMENT_CENTER && showPaymentCenterDot) indicator = "dot"
        else if (option === NavOption.COMPLIANCE) indicator = getComplianceIndicator()
        return (
          <NavItem
            key={option}
            label={option}
            isActive={checkIsCurrentNavOption(option)}
            indicator={indicator}
            onNavItemClick={onNavItemClick}
          />
        )
      })}
      <Spacer />
      {dealManager ? <VStack borderRadius={10} bg="whiteAlpha.100" p={5} ml={5} mr={5}>
        <Text fontSize="2xs" textAlign="center">
          Your Equity Partner
        </Text>
        <Image
          src={dealManager.profilePicture}
          borderRadius={10}
          boxSize="90px"
          alignSelf="center"
        />
        <Text fontSize="lg" fontWeight="bold" mt={3} textAlign="center">
          { dealManager.firstName } { dealManager.lastName }
        </Text>
        <Text fontSize="xs" textAlign="center">
          { dealManager.email }
        </Text>
        <Button
          variant="outline"
          color="white"
          size="sm"
          mt={3}
          w="100%"
          onClick={() => window.open(dealManager.calendly, "_blank", "noopener,noreferrer")}
          _hover={{ bg: "whiteAlpha.200" }}
        >
          Schedule Call
        </Button>
      </VStack> : null}
    </Stack>
  )
}